import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// PRO Auth Context
const ProAuthContext = createContext(null);

export const useProAuth = () => {
  const context = useContext(ProAuthContext);
  if (!context) {
    throw new Error('useProAuth must be used within ProAuthProvider');
  }
  return context;
};

export const ProAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check auth status on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pro-auth/me`, {
        credentials: 'include'  // Send cookies
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  // Google OAuth login - redirect to Emergent Auth
  const loginWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/pro/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  // Email/password login
  const loginWithEmail = async (email, password) => {
    const response = await fetch(`${API_URL}/api/pro-auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }

    const userData = await response.json();
    setUser(userData);
    setIsAuthenticated(true);
    return userData;
  };

  // Email/password registration
  const registerWithEmail = async (email, password, name) => {
    const response = await fetch(`${API_URL}/api/pro-auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, name })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Registration failed');
    }

    const userData = await response.json();
    setUser(userData);
    setIsAuthenticated(true);
    return userData;
  };

  // Exchange session_id from OAuth redirect
  const exchangeSession = async (sessionId) => {
    const response = await fetch(`${API_URL}/api/pro-auth/session`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'X-Session-ID': sessionId
      },
      credentials: 'include',
      body: JSON.stringify({ session_id: sessionId })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Session exchange failed');
    }

    const userData = await response.json();
    setUser(userData);
    setIsAuthenticated(true);
    return userData;
  };

  // Logout
  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/pro-auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setIsAuthenticated(false);
  };

  // Refresh user data (after subscription change)
  const refreshUser = async () => {
    await checkAuth();
  };

  // Check if user has active PRO subscription
  const isPro = user?.subscription?.status === 'active' || user?.subscription?.status === 'trialing';

  return (
    <ProAuthContext.Provider value={{
      user,
      loading,
      isAuthenticated,
      isPro,
      loginWithGoogle,
      loginWithEmail,
      registerWithEmail,
      exchangeSession,
      logout,
      refreshUser
    }}>
      {children}
    </ProAuthContext.Provider>
  );
};

export default ProAuthProvider;
